// ==========================================================================
// screens/familyTreeArtwork.tsx — the two drawings of the family tree
// (FF-057 v2, 10 Oct 2026). Started from ChatGPT's Draft_v1 pack (lineage
// rings, tracing, keyboard focus, export-safe colours) and pushed toward the
// concept art James liked: a watercolour scene, a real trunk and boughs, leafy
// canopy, handwritten names on little plaques, hearts between couples, and a
// chart of soft cards in generation bands.
//   Everything is inline SVG drawn from theme tokens (resolved to plain colours
//   by FamilyTree's useColours so "Save as a picture" keeps them). No images,
//   no filters, no new libraries. Leaves thin out as the tree grows.
//   Every coloured line stands for a recorded parent link; the trunk, the
//   boughs up to the youngest and the scenery are decoration only.
// ==========================================================================
import { memo, type ReactNode } from "react";
import { bornYear, fullName, isGone, years, type Layout, type TreeLink, type TreePerson } from "@/lib/tree";
import { FAMILY_PALETTE, type Ancestry, type Bloodlines, type TreeTrace } from "@/lib/familyTreeLineage";

export const ART_DIM = {
  tree: { sw: 184, rh: 240, padX: 250, padY: 96, r: 46, extra: 200 },
  chart: { sw: 196, rh: 236, padX: 230, padY: 70, cw: 150, ch: 170, extra: 140 },
} as const;

export type TreeColours = { tok: (name: string, alpha?: number) => string; dark: boolean };
export type ArtworkProps = {
  L: Layout;
  people: TreePerson[];
  links: TreeLink[];
  bloodlines: Bloodlines;
  trace: TreeTrace;
  C: TreeColours;
  W: number;
  H: number;
  sel: string | null;
  focusId: string | null;
  px: (id: string) => number;
  py: (id: string) => number;
  scale: number;
  onPick: (id: string) => void;
  onHover: (id: string | null) => void;
};

const HAND = "Caveat, 'Segoe Print', cursive";
const SERIF = "Cinzel, Georgia, serif";
const SANS = "'Plus Jakarta Sans', -apple-system, 'Segoe UI', sans-serif";

export const initialsOf = (p: TreePerson) => ((p.given_name[0] ?? "?") + (p.family_name?.[0] ?? "")).toUpperCase();
const textName = (p: TreePerson) => p.nickname || p.given_name;
const short = (s: string, limit = 18) => s.length > limit ? `${s.slice(0, limit - 1)}…` : s;
const hash = (s: string) => { let h = 2166136261; for (let i = 0; i < s.length; i++) h = Math.imul(h ^ s.charCodeAt(i), 16777619); return h >>> 0; };
export const lineColour = (C: TreeColours, i: number, alpha = 1) => C.tok(FAMILY_PALETTE[i % FAMILY_PALETTE.length].token, alpha);
const ancestryOf = (b: Bloodlines, id: string): Ancestry => b.byPerson.get(id) ?? { parts: [], dominant: 0 };
const dim = (focusId: string | null, trace: TreeTrace, id: string) => !focusId || trace.ids.has(id) ? 1 : 0.26;
const yearLine = (p: TreePerson) => isGone(p) ? years(p) : bornYear(p) ? `Born ${bornYear(p)}` : "";

/** The family's initial for the heart carved in the trunk: the commonest surname among the youngest. */
function carvedInitial(people: TreePerson[]) {
  const n = new Map<string, number>();
  for (const p of people) if (p.family_name) n.set(p.family_name, (n.get(p.family_name) ?? 0) + 1);
  const top = [...n].sort((a, b) => b[1] - a[1])[0]?.[0];
  if (!top) return "";
  return top.startsWith("O'") || top.startsWith("O’") ? "O’" : top[0].toUpperCase();
}

// ---------- small pieces ----------
function Leaf({ x, y, rotate = 0, size = 1, fill, vein }: { x: number; y: number; rotate?: number; size?: number; fill: string; vein?: string }) {
  return <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${size})`}>
    <path d="M0,0 C5,-10 18,-14 30,-2 C19,10 6,10 0,0 Z" fill={fill} />
    {vein && <path d="M2,-0.5 Q15,-3 27,-2" fill="none" stroke={vein} strokeWidth={0.9} />}
  </g>;
}

function Heart({ x, y, s = 1, fill }: { x: number; y: number; s?: number; fill: string }) {
  return <path d="M0,6 C-9,-2 -6,-10 0,-5 C6,-10 9,-2 0,6 Z" transform={`translate(${x} ${y}) scale(${s})`} fill={fill} />;
}

/** A little dove: "in loving memory". */
function Dove({ x, y, fill }: { x: number; y: number; fill: string }) {
  return <path transform={`translate(${x} ${y})`} fill={fill}
    d="M0,4 C3,-2 9,-3 12,-1 C14,-5 18,-7 21,-6 C19,-4 18,-2 18,0 C21,1 23,3 24,5 C19,4 15,5 12,7 C8,9 3,8 0,4 Z" />;
}

/** A sprig of leaves (chart bands, card tops). */
function Sprig({ x, y, rotate = 0, s = 1, C }: { x: number; y: number; rotate?: number; s?: number; C: TreeColours }) {
  return <g transform={`translate(${x} ${y}) rotate(${rotate}) scale(${s})`}>
    <path d="M0,0 C8,-14 18,-26 30,-36" stroke={C.tok("tree-bark", 0.7)} strokeWidth={1.4} fill="none" />
    {[[6, -9, -70], [12, -17, 30], [17, -23, -60], [23, -29, 40], [29, -35, -20]].map(([lx, ly, r], i) =>
      <Leaf key={i} x={lx} y={ly} rotate={r} size={0.42 + (i % 2) * 0.08} fill={C.tok(i % 2 ? "tree-leaf-light" : "tree-leaf", 0.85)} />)}
  </g>;
}

/** Ring segments: each recorded founder branch gets its share of the ring. */
function LineageRing({ ancestry, C, x, y, radius, width = 5 }: { ancestry: Ancestry; C: TreeColours; x: number; y: number; radius: number; width?: number }) {
  const ring = [...ancestry.parts].sort((a, b) => b.fraction - a.fraction).slice(0, 8);
  const total = ring.reduce((s, p) => s + p.fraction, 0) || 1;
  const circ = 2 * Math.PI * radius;
  let at = 0;
  return <g transform={`rotate(-90 ${x} ${y})`}>
    {ring.map((part) => {
      const f = part.fraction / total; const start = at; at += f;
      const slice = Math.max(0, f * circ - (ring.length > 1 ? 3 : 0));
      return <circle key={part.originId} cx={x} cy={y} r={radius} fill="none" stroke={lineColour(C, part.paletteIndex)}
        strokeWidth={width} strokeDasharray={`${slice} ${circ - slice}`} strokeDashoffset={-start * circ} />;
    })}
  </g>;
}

function Hit({ p, children, onPick, onHover }: { p: TreePerson; children: ReactNode; onPick: (id: string) => void; onHover: (id: string | null) => void }) {
  return <g data-person={p.id} tabIndex={0} role="button" aria-label={`${fullName(p)}. ${years(p)}`}
    onPointerEnter={(e) => { if (e.pointerType === "mouse" || e.pointerType === "pen") onHover(p.id); }}
    onPointerLeave={(e) => { if (e.pointerType === "mouse" || e.pointerType === "pen") onHover(null); }}
    onFocus={() => onHover(p.id)} onBlur={() => onHover(null)}
    onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onPick(p.id); } }}>
    {children}
  </g>;
}

/** Handwritten words in the margins. */
function Script({ x, y, lines, rotate = -12, size = 30, C }: { x: number; y: number; lines: string[]; rotate?: number; size?: number; C: TreeColours }) {
  return <text x={x} y={y} transform={`rotate(${rotate} ${x} ${y})`} fontFamily={HAND} fontSize={size} fontWeight={500} fill={C.tok("text-muted", 0.62)}>
    {lines.map((l, i) => <tspan key={i} x={x + i * 14} dy={i ? size * 0.95 : 0}>{l}</tspan>)}
  </text>;
}

/** Soft watercolour washes, distant trees and a meadow. Drawn into the SVG so the saved picture has them. */
function Scenery({ W, H, ground, C, flowersAt }: { W: number; H: number; ground: number; C: TreeColours; flowersAt?: { x: number; spread: number } }) {
  const washes = [
    { cx: W * 0.84, cy: H * 0.12, rx: W * 0.32, ry: H * 0.26, c: "primary", a: C.dark ? 0.07 : 0.10 },
    { cx: W * 0.12, cy: H * 0.2, rx: W * 0.3, ry: H * 0.24, c: "tree-leaf", a: C.dark ? 0.06 : 0.09 },
    { cx: W * 0.5, cy: H * 0.45, rx: W * 0.45, ry: H * 0.3, c: "accent", a: C.dark ? 0.04 : 0.08 },
    { cx: W * 0.2, cy: H * 0.9, rx: W * 0.35, ry: H * 0.2, c: "tree-leaf-light", a: C.dark ? 0.06 : 0.14 },
  ];
  const far = [0.04, 0.1, 0.17, 0.83, 0.9, 0.96];
  return <>
    <defs>
      <linearGradient id="ft-paper" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0" stopColor={C.tok("tree-paper")} />
        <stop offset="0.7" stopColor={C.tok("tree-wash", C.dark ? 0.55 : 0.6)} />
        <stop offset="1" stopColor={C.tok("tree-paper")} />
      </linearGradient>
      {washes.map((w, i) => <radialGradient key={i} id={`ft-wash-${i}`}>
        <stop offset="0" stopColor={C.tok(w.c, w.a)} /><stop offset="1" stopColor={C.tok(w.c, 0)} />
      </radialGradient>)}
    </defs>
    <rect x={-3000} y={-3000} width={W + 6000} height={H + 6000} fill={C.tok("tree-paper")} />
    <rect x={-3000} y={0} width={W + 6000} height={H} fill="url(#ft-paper)" />
    {washes.map((w, i) => <ellipse key={i} cx={w.cx} cy={w.cy} rx={w.rx} ry={w.ry} fill={`url(#ft-wash-${i})`} />)}
    {/* far hills and little trees */}
    <path d={`M-3000 ${ground - 24} H0 C${W * 0.18} ${ground - 70} ${W * 0.32} ${ground - 18} ${W * 0.5} ${ground - 34} S${W * 0.82} ${ground - 72} ${W} ${ground - 30} H${W + 3000} V${H + 3000} H-3000 Z`} fill={C.tok("tree-leaf", C.dark ? 0.07 : 0.1)} />
    {far.map((f, i) => {
      const x = W * f, h = 46 + (i % 3) * 18, y = ground - 30 - (i % 2) * 8;
      return <g key={i} opacity={C.dark ? 0.35 : 0.6}>
        <path d={`M${x} ${y} V${y - h * 0.55}`} stroke={C.tok("tree-bark", 0.6)} strokeWidth={3.5} />
        <ellipse cx={x} cy={y - h * 0.62} rx={h * 0.38} ry={h * 0.42} fill={C.tok("tree-leaf-light", 0.6)} />
        <ellipse cx={x - h * 0.14} cy={y - h * 0.72} rx={h * 0.24} ry={h * 0.27} fill={C.tok("tree-leaf", 0.35)} />
        <ellipse cx={x + h * 0.16} cy={y - h * 0.5} rx={h * 0.22} ry={h * 0.22} fill={C.tok("tree-leaf-deep", 0.25)} />
      </g>;
    })}
    <path d={`M-3000 ${ground + 6} H0 C${W * 0.25} ${ground - 22} ${W * 0.55} ${ground + 18} ${W} ${ground - 4} H${W + 3000} V${H + 3000} H-3000 Z`} fill={C.tok("tree-leaf-light", C.dark ? 0.12 : 0.28)} />
    {/* meadow: grass tufts and tiny flowers, more of them near the trunk */}
    {Array.from({ length: 70 }, (_, i) => {
      const h = hash(`grass${i}`);
      const x = flowersAt && i < 40 ? flowersAt.x + ((h % 1000) / 1000 - 0.5) * flowersAt.spread : (h % 10000) / 10000 * W;
      const y = ground + 4 + (h >>> 10) % 26;
      const flower = i % 4 === 0;
      return <g key={i}>
        <path d={`M${x} ${y} q-3 -9 -1 -16 M${x} ${y} q2 -8 6 -13`} stroke={C.tok("tree-leaf", 0.55)} strokeWidth={1.1} fill="none" />
        {flower && <g>
          {[0, 72, 144, 216, 288].map((a) => <circle key={a} cx={x - 1 + Math.cos(a * Math.PI / 180) * 3} cy={y - 17 + Math.sin(a * Math.PI / 180) * 3} r={2.4} fill={i % 8 === 0 ? C.tok("tree-paper") : C.tok("primary", 0.45)} />)}
          <circle cx={x - 1} cy={y - 17} r={1.5} fill={C.tok("tree-line-2", 0.8)} />
        </g>}
      </g>;
    })}
    {/* clouds */}
    {[[0.12, 0.08, 1.2], [0.3, 0.05, 0.8], [0.72, 0.07, 1.0], [0.9, 0.14, 0.7]].map(([fx, fy, sc], i) => <g key={`cloud-${i}`} transform={`translate(${W * fx} ${H * fy}) scale(${sc})`} opacity={C.dark ? 0.08 : 0.55}>
      <ellipse cx={0} cy={0} rx={60} ry={22} fill={C.tok("surface")} /><ellipse cx={-30} cy={6} rx={38} ry={18} fill={C.tok("surface")} /><ellipse cx={34} cy={4} rx={42} ry={20} fill={C.tok("surface")} /><ellipse cx={6} cy={-10} rx={34} ry={20} fill={C.tok("surface")} />
    </g>)}
    {/* birds */}
    {[[0.06, 0.1, 1], [0.1, 0.07, 0.8], [0.92, 0.09, 1.1]].map(([fx, fy, s], i) =>
      <path key={i} d="M0,0 q7,-7 14,0 q7,-7 14,0" transform={`translate(${W * fx} ${H * fy}) scale(${s * 1.8})`} stroke={C.tok("primary", 0.45)} strokeWidth={1.4} fill="none" strokeLinecap="round" />)}
  </>;
}

// ===================================================================== TREE
// Painterly pieces: a tapered bough, foliage clumps, bark.
const GREENS = ["tree-leaf", "tree-leaf-light", "tree-leaf-deep", "tree-leaf", "tree-leaf-light"];

/** A bough that gets thinner from a (thick end) to b (thin end): short round-capped segments. */
function Bough({ a, b, w0, w1, C, bend = 0.5, op = 1, k }: { a: { x: number; y: number }; b: { x: number; y: number }; w0: number; w1: number; C: TreeColours; bend?: number; op?: number; k: string }) {
  const N = 9;
  const pts = Array.from({ length: N + 1 }, (_, i) => bez(a, b, i / N, bend));
  const segs = pts.slice(1).map((pt, i) => ({ d: `M${pts[i].x},${pts[i].y} L${pt.x},${pt.y}`, w: w0 + (w1 - w0) * ((i + 0.5) / N) }));
  return <g data-trace-opacity opacity={op} key={k}>
    {segs.map((sg, i) => <path key={`s${i}`} d={sg.d} stroke={C.tok("tree-bark-dark", 0.55)} strokeWidth={sg.w + 3.5} strokeLinecap="round" fill="none" />)}
    {segs.map((sg, i) => <path key={`m${i}`} d={sg.d} stroke={C.tok("tree-bark")} strokeWidth={sg.w} strokeLinecap="round" fill="none" />)}
    {segs.map((sg, i) => <path key={`h${i}`} d={sg.d} stroke={C.tok("tree-bark-light", 0.5)} strokeWidth={Math.max(1, sg.w * 0.3)} strokeLinecap="round" fill="none" transform="translate(-1.5 -1)" />)}
    {segs.filter((_, i) => i % 3 === 1).map((sg, i) => <path key={`g${i}`} d={sg.d} stroke={C.tok("tree-bark-dark", 0.3)} strokeWidth={0.9} strokeLinecap="round" fill="none" transform="translate(2 1.5)" />)}
  </g>;
}
function bez(a: { x: number; y: number }, b: { x: number; y: number }, t: number, bend: number) {
  const dy = Math.abs(b.y - a.y) * bend;
  const p1 = { x: a.x, y: a.y - dy }, p2 = { x: b.x, y: b.y + dy }, u = 1 - t;
  return { x: u * u * u * a.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * b.x, y: u * u * u * a.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * b.y };
}

/** A clump of foliage: a soft blob behind, then leaves in a few greens, a blossom now and then. */
function Clump({ x, y, r, n, seed, C, blossoms = true, op = 1 }: { x: number; y: number; r: number; n: number; seed: string; C: TreeColours; blossoms?: boolean; op?: number }) {
  const h0 = hash(seed);
  const leaves: ReactNode[] = [];
  for (let i = 0; i < n; i++) {
    const h = hash(`${seed}:${i}`);
    const ang = (h % 3600) / 3600 * Math.PI * 2;
    const dist = Math.sqrt(((h >>> 8) % 1000) / 1000) * r;
    const lx = x + Math.cos(ang) * dist * 1.15, ly = y + Math.sin(ang) * dist * 0.85;
    const pick = (h >>> 4) % 100;
    const fill = blossoms && pick < 4 ? C.tok("primary", 0.5) : pick < 8 ? C.tok("tree-line-2", 0.55) : C.tok(GREENS[(h >>> 12) % GREENS.length], 0.92);
    const size = 0.8 + ((h >>> 6) % 6) * 0.14;
    if ((h >>> 10) % 2 === 0) leaves.push(<Leaf key={`u${i}`} x={lx + 1.5} y={ly + 2} rotate={(h >>> 3) % 360} size={size} fill={C.tok("tree-leaf-deep", 0.35)} />);
    leaves.push(<Leaf key={i} x={lx} y={ly} rotate={(h >>> 3) % 360} size={size} fill={fill} vein={(h >>> 9) % 3 === 0 ? C.tok("tree-paper", 0.45) : undefined} />);
  }
  return <g data-trace-opacity opacity={op}>
    <ellipse cx={x + ((h0 % 20) - 10)} cy={y} rx={r * 1.1} ry={r * 0.82} fill={C.tok("tree-leaf-deep", C.dark ? 0.12 : 0.06)} />
    <ellipse cx={x - ((h0 >>> 5) % 16) + 8} cy={y - r * 0.2} rx={r * 0.8} ry={r * 0.6} fill={C.tok("tree-leaf", C.dark ? 0.1 : 0.05)} />
    {leaves}
  </g>;
}

/** Blossom: five petals and a yellow heart. */
function Flower({ x, y, s = 1, C }: { x: number; y: number; s?: number; C: TreeColours }) {
  return <g transform={`translate(${x} ${y}) scale(${s})`}>
    {[0, 72, 144, 216, 288].map((a) => <ellipse key={a} cx={Math.cos(a * Math.PI / 180) * 4} cy={Math.sin(a * Math.PI / 180) * 4} rx={3.2} ry={2.4} transform={`rotate(${a} ${Math.cos(a * Math.PI / 180) * 4} ${Math.sin(a * Math.PI / 180) * 4})`} fill={C.tok("primary", 0.55)} />)}
    <circle r={1.6} fill={C.tok("tree-line-2", 0.9)} />
  </g>;
}

/** A real tree: the youngest sit low on boughs from the trunk; parents above
 *  them; every coloured line under a person is a recorded parent link. */
export const TreeArtwork = memo(function TreeArtwork({ L, people, links, bloodlines, C, W, H, px, py, focusId, sel, trace, scale, onPick, onHover }: ArtworkProps) {
  const r = ART_DIM.tree.r;
  const placed = people.filter((p) => L.nodes.has(p.id));
  const youngest = placed.filter((p) => L.nodes.get(p.id)!.gen === L.gens - 1);
  const baseX = youngest.length ? youngest.reduce((s, p) => s + px(p.id), 0) / youngest.length : W / 2;
  const lowY = youngest.length ? Math.max(...youngest.map((p) => py(p.id))) : H * 0.6;
  const ground = H - 70;
  const trunkTop = lowY + r + 76;
  const many = placed.length;
  const density = many < 25 ? 1 : many < 60 ? 0.6 : many < 120 ? 0.3 : 0.1;
  const parentLinkId = new Map(links.filter((l) => l.kind === "parent").map((l) => [`${l.from_id}|${l.to_id}`, l.id]));
  const coupleIds = new Set(L.partners.map((l) => [l.from_id, l.to_id].sort().join("|")));
  const dimmed = (id: string) => dim(focusId, trace, id) < 1;

  const crowns: ReactNode[] = [];   // big soft foliage behind everything above the trunk
  const boughs: ReactNode[] = [];
  const stripes: ReactNode[] = [];
  const foliage: ReactNode[] = [];  // leaves drawn over the boughs
  const twigs: ReactNode[] = [];

  // Crown: foliage clumps around every person and along every bough.
  for (const p of placed) {
    const x = px(p.id), y = py(p.id), g = L.nodes.get(p.id)!.gen;
    const low = g === L.gens - 1 && L.gens > 1;
    const op = dimmed(p.id) ? 0.45 : 1;
    crowns.push(<Clump key={`crown-${p.id}`} x={x} y={y - 34} r={low ? 76 : 124} n={Math.round((low ? 40 : 90) * density)} seed={`crown${p.id}`} C={C} op={op} />);
    foliage.push(<Clump key={`fol-${p.id}`} x={x + 12} y={y - r - 40} r={low ? 46 : 68} n={Math.round((low ? 14 : 30) * density)} seed={`fol${p.id}`} C={C} op={op} />);
    foliage.push(<Clump key={`fol2-${p.id}`} x={x - 70} y={y + 10} r={34} n={Math.round(10 * density)} seed={`fol2${p.id}`} C={C} op={op} blossoms={false} />);
  }

  // Families: each child's bough climbs to a knot under its parents.
  for (const f of L.families) {
    const ps = f.parents.filter((id) => L.nodes.has(id));
    const kids = f.children.filter((id) => L.nodes.has(id));
    if (!ps.length || !kids.length) continue;
    const jx = ps.reduce((s, id) => s + px(id), 0) / ps.length;
    const jy = Math.max(...ps.map(py)) + r + 54;
    const kidGen = Math.min(...kids.map((k) => L.nodes.get(k)!.gen));
    const w = Math.max(9, 22 - kidGen * 2.5);
    const lit = (child: string) => !focusId || ps.some((pp) => trace.parentLinkIds.has(parentLinkId.get(`${pp}|${child}`) ?? ""));
    for (const pid of ps) {
      const couple = ps.length === 2 && coupleIds.has([...ps].sort().join("|"));
      const target = { x: px(pid), y: py(pid) + r - 4 };
      const forkLit = !focusId || kids.some((k) => trace.parentLinkIds.has(parentLinkId.get(`${pid}|${k}`) ?? ""));
      boughs.push(<Bough key={`fork-${pid}-${kids.join()}`} k={`fork-${pid}`} a={{ x: jx, y: jy }} b={target} w0={w * 0.8} w1={w * 0.45} C={C} bend={couple ? 0.6 : 0.2} op={forkLit ? 1 : 0.3} />);
      const a = ancestryOf(bloodlines, pid);
      const d = `M${jx},${jy} C${jx},${jy - Math.abs(target.y - jy) * (couple ? 0.6 : 0.2)} ${target.x},${target.y + Math.abs(target.y - jy) * (couple ? 0.6 : 0.2)} ${target.x},${target.y}`;
      stripes.push(<path key={`fs-${pid}-${kids.join()}`} data-trace-opacity opacity={forkLit ? 1 : 0.3} d={d} stroke={lineColour(C, a.dominant, 0.95)}
        strokeWidth={focusId && forkLit ? 3.6 : 2.4} strokeDasharray={FAMILY_PALETTE[a.dominant]?.dash} strokeLinecap="round" fill="none" />);
    }
    for (const kid of kids) {
      const from = { x: px(kid), y: py(kid) - r + 2 };
      boughs.push(<Bough key={`up-${kid}`} k={`up-${kid}`} a={from} b={{ x: jx, y: jy }} w0={w} w1={w * 0.8} C={C} bend={0.55} op={lit(kid) ? 1 : 0.3} />);
      // small twigs with leaves off the bough
      for (let i = 1; i <= 2; i++) {
        const pt = bez(from, { x: jx, y: jy }, i / 3, 0.55);
        const h = hash(`${kid}:${i}`); const side = i % 2 ? 1 : -1;
        twigs.push(<g key={`twig-${kid}-${i}`} data-trace-opacity opacity={lit(kid) ? 1 : 0.3}>
          <path d={`M${pt.x} ${pt.y} q${side * 14} -${14 + h % 10} ${side * 30} -${16 + h % 12}`} stroke={C.tok("tree-bark")} strokeWidth={2.6} fill="none" strokeLinecap="round" />
          <Clump x={pt.x + side * 32} y={pt.y - 20 - h % 12} r={30} n={Math.round(14 * density)} seed={`tw${kid}${i}`} C={C} />
        </g>);
      }
      const d = `M${from.x},${from.y} C${from.x},${from.y - Math.abs(jy - from.y) * 0.55} ${jx},${jy + Math.abs(jy - from.y) * 0.55} ${jx},${jy}`;
      ps.forEach((pid, i) => {
        const a = ancestryOf(bloodlines, pid);
        stripes.push(<path key={`ks-${kid}-${pid}`} data-trace-opacity opacity={lit(kid) ? 1 : 0.3} d={d}
          transform={ps.length === 2 ? `translate(${i ? 2.2 : -2.2} 0)` : undefined} stroke={lineColour(C, a.dominant, 0.95)}
          strokeWidth={focusId && lit(kid) ? 3.4 : 2.2} strokeDasharray={FAMILY_PALETTE[a.dominant]?.dash} strokeLinecap="round" fill="none" />);
      });
      if (focusId && lit(kid)) stripes.push(<path key={`hl-${kid}`} data-trace-highlight d={d} stroke={C.tok("primary", 0.3)} strokeWidth={w + 8} strokeLinecap="round" fill="none" />);
    }
    boughs.push(<g key={`knot-${ps.join()}-${kids.join()}`}>
      <circle cx={jx} cy={jy} r={w * 0.7 + 2} fill={C.tok("tree-bark")} stroke={C.tok("tree-bark-dark", 0.6)} strokeWidth={2.5} />
      <circle cx={jx - w * 0.2} cy={jy - w * 0.2} r={w * 0.25} fill={C.tok("tree-bark-light", 0.5)} />
    </g>);
  }

  // Partners: a leafy vine with a heart (dashed and no heart once apart).
  const vines = L.partners.map((link) => {
    if (!L.nodes.has(link.from_id) || !L.nodes.has(link.to_id)) return null;
    const [a, b] = [link.from_id, link.to_id].sort((m, n) => px(m) - px(n));
    const x1 = px(a) + r + 8, x2 = px(b) - r - 8, y = py(a) + 4;
    if (x2 <= x1) return null;
    const apart = link.status === "separated" || link.status === "divorced";
    const on = !focusId || trace.partnerLinkIds.has(link.id);
    const mx = (x1 + x2) / 2;
    return <g key={`vine-${link.id}`} data-trace-opacity opacity={on ? 1 : 0.25}>
      <path d={`M${x1} ${y} Q${mx} ${y + 18} ${x2} ${y}`} stroke={C.tok("tree-leaf-deep")} strokeWidth={2.4} fill="none" strokeDasharray={apart ? "5 6" : undefined} />
      {!apart && <>
        <Leaf x={mx - 26} y={y + 10} rotate={200} size={0.42} fill={C.tok("tree-leaf-light")} />
        <Leaf x={mx + 26} y={y + 10} rotate={-20} size={0.42} fill={C.tok("tree-leaf-light")} />
        <circle cx={mx} cy={y + 9} r={12} fill={C.tok("surface")} stroke={C.tok("primary", 0.4)} strokeWidth={1.2} />
        <Heart x={mx} y={y + 9} s={1} fill={C.tok("primary")} />
      </>}
    </g>;
  });

  const initial = carvedInitial(youngest.length ? youngest : placed);
  const tw = 64; // half trunk width at the ground
  const trunkH = ground - trunkTop;

  return <>
    <Scenery W={W} H={H} ground={ground} C={C} flowersAt={{ x: baseX, spread: 520 }} />
    <Script x={40} y={H * 0.34} lines={["Stronger", "together ♡"]} C={C} size={34} />
    <Script x={W - 215} y={H - 200} lines={["Same roots,", "brighter", "tomorrows ♡"]} C={C} rotate={-14} size={32} />
    {crowns}
    <defs>
      <linearGradient id="ft-trunk" x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor={C.tok("tree-bark-dark")} />
        <stop offset="0.3" stopColor={C.tok("tree-bark")} />
        <stop offset="0.62" stopColor={C.tok("tree-bark-light")} />
        <stop offset="0.85" stopColor={C.tok("tree-bark")} />
        <stop offset="1" stopColor={C.tok("tree-bark-dark")} />
      </linearGradient>
      <radialGradient id="ft-shade"><stop offset="0" stopColor={C.tok("tree-bark-dark", 0.35)} /><stop offset="1" stopColor={C.tok("tree-bark-dark", 0)} /></radialGradient>
    </defs>
    {/* shadow on the grass */}
    <ellipse cx={baseX} cy={ground + 14} rx={tw + 150} ry={22} fill="url(#ft-shade)" />
    {/* limbs from the trunk up to the youngest (decoration: the trunk is the family, not a link) */}
    {youngest.map((p) => <Bough key={`low-${p.id}`} k={`low-${p.id}`} a={{ x: baseX, y: trunkTop + 14 }} b={{ x: px(p.id), y: py(p.id) + r - 4 }} w0={26} w1={14} C={C} bend={0.5} />)}
    {/* the trunk: bulging, with roots */}
    <path fill="url(#ft-trunk)" d={`M${baseX - tw - 50} ${ground + 14}
      C${baseX - tw - 10} ${ground - 4} ${baseX - tw + 12} ${ground - trunkH * 0.35} ${baseX - 30} ${trunkTop + 36}
      C${baseX - 26} ${trunkTop + 12} ${baseX + 26} ${trunkTop + 12} ${baseX + 30} ${trunkTop + 36}
      C${baseX + tw - 12} ${ground - trunkH * 0.35} ${baseX + tw + 10} ${ground - 4} ${baseX + tw + 50} ${ground + 14}
      Q${baseX + 20} ${ground - 2} ${baseX} ${ground + 4} Q${baseX - 20} ${ground - 2} ${baseX - tw - 50} ${ground + 14} Z`} />
    {/* bark */}
    {[-22, -12, -3, 6, 15, 24].map((dx, i) => <path key={`bark-${i}`}
      d={`M${baseX + dx * 1.6} ${ground - 6} C${baseX + dx - 9} ${ground - trunkH * 0.4} ${baseX + dx + 11} ${trunkTop + trunkH * 0.45} ${baseX + dx * 0.7} ${trunkTop + 30}`}
      stroke={i % 2 ? C.tok("tree-bark-dark", 0.42) : C.tok("tree-bark-light", 0.4)} strokeWidth={i % 2 ? 2.8 : 1.4} fill="none" strokeLinecap="round" />)}
    {[0.25, 0.55, 0.78].map((f, i) => <ellipse key={`knot-${i}`} cx={baseX + (i % 2 ? 18 : -16)} cy={trunkTop + trunkH * f} rx={7} ry={4.5} fill={C.tok("tree-bark-dark", 0.35)} stroke={C.tok("tree-bark-light", 0.35)} strokeWidth={1} />)}
    {/* moss near the base */}
    <ellipse cx={baseX - 22} cy={ground - 20} rx={16} ry={9} fill={C.tok("tree-leaf-deep", 0.35)} />
    <ellipse cx={baseX + 30} cy={ground - 12} rx={12} ry={7} fill={C.tok("tree-leaf", 0.35)} />
    {/* roots */}
    {[-1, 1].map((n) => <g key={n}>
      <path d={`M${baseX + n * 36} ${ground + 6} q${n * 40} 2 ${n * 86} 22 q${n * 26} 8 ${n * 56} 6`} stroke={C.tok("tree-bark-dark", 0.5)} strokeWidth={13} strokeLinecap="round" fill="none" />
      <path d={`M${baseX + n * 36} ${ground + 6} q${n * 40} 2 ${n * 86} 22 q${n * 26} 8 ${n * 56} 6`} stroke={C.tok("tree-bark")} strokeWidth={9} strokeLinecap="round" fill="none" />
      <path d={`M${baseX + n * 20} ${ground + 10} q${n * 20} 10 ${n * 40} 30`} stroke={C.tok("tree-bark")} strokeWidth={6} strokeLinecap="round" fill="none" />
    </g>)}
    {initial && trunkH > 150 && <g>
      <path d="M0,24 C-32,2 -28,-26 0,-13 C28,-26 32,2 0,24 Z" transform={`translate(${baseX} ${ground - trunkH * 0.45})`} fill={C.tok("tree-bark-dark", 0.2)} stroke={C.tok("tree-bark-dark", 0.6)} strokeWidth={2.2} />
      <text x={baseX} y={ground - trunkH * 0.45 + 8} textAnchor="middle" fontFamily={SERIF} fontSize={19} fontWeight={600} fill={C.tok("tree-bark-dark", 0.75)}>{initial}</text>
    </g>}
    {boughs}
    {stripes}
    {twigs}
    {foliage}
    {vines}
    {placed.map((p) => {
      const x = px(p.id), y = py(p.id), a = ancestryOf(bloodlines, p.id);
      const gone = isGone(p), active = sel === p.id || focusId === p.id;
      const main = lineColour(C, a.dominant);
      const surname = p.family_name ? short(p.family_name, 14) : "";
      const plateH = surname ? 58 : 46;
      return <Hit key={p.id} p={p} onPick={onPick} onHover={onHover}>
        <g data-trace-opacity opacity={dim(focusId, trace, p.id)}>
          {active && <g data-selection-halo>
            <circle cx={x} cy={y} r={r + 26} fill={C.tok("primary", 0.14)} />
            <circle cx={x} cy={y} r={r + 16} fill="none" stroke={C.tok("primary", 0.55)} strokeWidth={3} />
          </g>}
          <circle cx={x + 1} cy={y + 5} r={r + 10} fill={C.tok("tree-bark-dark", 0.16)} />
          <circle cx={x} cy={y} r={r + 9} fill={C.tok("surface")} />
          <circle cx={x} cy={y} r={r - 3} fill={lineColour(C, a.dominant, C.dark ? 0.22 : 0.1)} />
          <LineageRing ancestry={a} C={C} x={x} y={y} radius={r + 2} width={6.5} />
          <text x={x} y={y + 9} textAnchor="middle" fontFamily={SERIF} fontSize={26} fontWeight={600} fill={main}>{initialsOf(p)}</text>
          <rect x={x - 66} y={y + r + 12} width={132} height={plateH + 8} rx={18} fill={C.tok("tree-bark-dark", 0.12)} transform="translate(0 3)" />
          <rect x={x - 66} y={y + r + 12} width={132} height={plateH + 8} rx={18} fill={C.tok("surface", 0.97)} />
          <text x={x} y={y + r + 40} textAnchor="middle" fontFamily={HAND} fontSize={29} fontWeight={700} fill={C.tok("text")}>{short(textName(p), 12)}</text>
          {surname && <text x={x} y={y + r + 56} textAnchor="middle" fontFamily={SANS} fontSize={12} fill={C.tok("text-muted")}>{surname}</text>}
          <text x={x} y={y + r + (surname ? 71 : 57)} textAnchor="middle" fontFamily={SANS} fontSize={12} fill={C.tok("text-muted")}>{yearLine(p)}</text>
          {gone && <Dove x={x - 62} y={y + r + 18} fill={C.tok("text-muted", 0.6)} />}
          <circle className="family-tree-focus" cx={x} cy={y + 14} r={Math.max(r + 26, 22 / Math.max(scale, 0.1))} fill="transparent" stroke="transparent" pointerEvents="all" />
        </g>
      </Hit>;
    })}
    {/* a few blossoms over the top of the canopy */}
    {density >= 0.6 && placed.filter((p) => L.nodes.get(p.id)!.gen === 0).flatMap((p) => [0, 1, 2].map((i) => {
      const h = hash(`bl${p.id}${i}`);
      return <Flower key={`bl-${p.id}-${i}`} x={px(p.id) + (h % 160) - 80} y={py(p.id) - r - 60 - (h >>> 8) % 50} s={0.8 + (h % 3) * 0.2} C={C} />;
    }))}
  </>;
});

// ==================================================================== CHART
/** Generation names counted from the youngest row. */
function genName(fromBottom: number) {
  if (fromBottom === 0) return "Children";
  if (fromBottom === 1) return "Parents";
  if (fromBottom === 2) return "Grandparents";
  return `${"Great-".repeat(fromBottom - 2)}grandparents`;
}

export const ChartArtwork = memo(function ChartArtwork({ L, people, links, bloodlines, C, W, H, px, py, focusId, sel, trace, scale, onPick, onHover }: ArtworkProps) {
  const { cw, ch, rh, padY: pad } = ART_DIM.chart;
  const placed = people.filter((p) => L.nodes.has(p.id));
  const top = (id: string) => py(id) - ch / 2;
  const avY = (id: string) => top(id) + 50;
  const ground = H - 80;
  const parentLinkId = new Map(links.filter((l) => l.kind === "parent").map((l) => [`${l.from_id}|${l.to_id}`, l.id]));
  const couples = new Map(L.partners.map((l) => [[l.from_id, l.to_id].sort().join("|"), l]));
  const rounded = (pts: [number, number][], rad = 14) => {
    let d = `M${pts[0][0]},${pts[0][1]}`;
    for (let i = 1; i < pts.length - 1; i++) {
      const [x0, y0] = pts[i - 1], [x1, y1] = pts[i], [x2, y2] = pts[i + 1];
      const r1 = Math.min(rad, Math.hypot(x1 - x0, y1 - y0) / 2, Math.hypot(x2 - x1, y2 - y1) / 2);
      const ax = x1 - Math.sign(x1 - x0) * r1, ay = y1 - Math.sign(y1 - y0) * r1;
      const bx = x1 + Math.sign(x2 - x1) * r1, by = y1 + Math.sign(y2 - y1) * r1;
      d += ` L${ax},${ay} Q${x1},${y1} ${bx},${by}`;
    }
    const last = pts[pts.length - 1];
    return `${d} L${last[0]},${last[1]}`;
  };

  const lines: ReactNode[] = [];
  for (const f of L.families) {
    const ps = f.parents.filter((id) => L.nodes.has(id));
    const kids = f.children.filter((id) => L.nodes.has(id));
    if (!ps.length || !kids.length) continue;
    const isCouple = ps.length === 2 && couples.has([...ps].sort().join("|"));
    const sx = ps.reduce((s, id) => s + px(id), 0) / ps.length;
    const sy = isCouple ? avY(ps[0]) + 14 : Math.max(...ps.map((id) => top(id) + ch));
    const busY = Math.max(...ps.map((id) => top(id) + ch)) + (rh - ch) / 2;
    for (const kid of kids) {
      const kx = px(kid), ky = top(kid) - 4;
      const d = rounded([[sx, sy], [sx, busY], [kx, busY], [kx, ky]]);
      const lit = !focusId || ps.some((pp) => trace.parentLinkIds.has(parentLinkId.get(`${pp}|${kid}`) ?? ""));
      lines.push(<g key={`ln-${ps.join()}-${kid}`} data-trace-opacity opacity={lit ? 1 : 0.3}>
        <path d={d} stroke={C.tok("tree-paper")} strokeWidth={8} fill="none" strokeLinejoin="round" />
        {ps.map((pid, i) => {
          const a = ancestryOf(bloodlines, pid);
          return <path key={pid} d={d} transform={ps.length === 2 ? `translate(${i ? 1.6 : -1.6} 0)` : undefined} stroke={lineColour(C, a.dominant, 0.85)}
            strokeWidth={2.4} strokeDasharray={FAMILY_PALETTE[a.dominant]?.dash} fill="none" strokeLinecap="round" strokeLinejoin="round" />;
        })}
        {focusId && lit && <path data-trace-highlight d={d} stroke={C.tok("primary", 0.9)} strokeWidth={3} fill="none" strokeLinecap="round" strokeLinejoin="round" />}
        <Leaf x={kx - 2} y={ky - 4} rotate={-140} size={0.4} fill={C.tok("tree-leaf")} />
        <Leaf x={kx + 2} y={ky - 4} rotate={-40} size={0.4} fill={C.tok("tree-leaf-light")} />
      </g>);
    }
  }
  const hearts = L.partners.map((link) => {
    if (!L.nodes.has(link.from_id) || !L.nodes.has(link.to_id)) return null;
    const [a, b] = [link.from_id, link.to_id].sort((m, n) => px(m) - px(n));
    const x1 = px(a) + cw / 2, x2 = px(b) - cw / 2, y = avY(a);
    if (x2 <= x1) return null;
    const apart = link.status === "separated" || link.status === "divorced";
    const on = !focusId || trace.partnerLinkIds.has(link.id);
    const mx = (x1 + x2) / 2;
    return <g key={`h-${link.id}`} data-trace-opacity opacity={on ? 1 : 0.25}>
      <path d={`M${x1} ${y} H${x2}`} stroke={C.tok("text-muted", 0.5)} strokeWidth={1.6} strokeDasharray={apart ? "4 4" : undefined} />
      {!apart && <>
        <Leaf x={mx - 6} y={y - 3} rotate={205} size={0.4} fill={C.tok("tree-leaf-light")} />
        <Leaf x={mx + 6} y={y - 3} rotate={-25} size={0.4} fill={C.tok("tree-leaf")} />
      </>}
      <circle cx={mx} cy={y} r={11} fill={C.tok("surface")} stroke={C.tok(apart ? "text-muted" : "primary", 0.5)} strokeWidth={1.4} />
      {apart ? <path d={`M${mx - 4} ${y - 4} l8 8 m0 -8 l-8 8`} stroke={C.tok("text-muted")} strokeWidth={1.5} />
        : <Heart x={mx} y={y} s={0.85} fill={C.tok("primary")} />}
    </g>;
  });

  return <>
    <Scenery W={W} H={H} ground={ground} C={C} />
    {Array.from({ length: L.gens }, (_, g) => {
      const y = pad + g * rh + (rh - ch) / 2 - 18;
      const h = ch + 36;
      return <g key={`band-${g}`}>
        <rect x={24} y={y} width={W - 48} height={h} rx={26} fill={C.tok("surface", C.dark ? 0.35 : 0.5)} stroke={C.tok("border", 0.5)} />
        <text x={50} y={y + 44} fontFamily={SANS} fontSize={17} fontWeight={600} fill={C.tok("text")}>Generation {g + 1}</text>
        <text x={50} y={y + 68} fontFamily={HAND} fontSize={26} fill={C.tok("text-muted")}>{genName(L.gens - 1 - g)}</text>
        <Sprig x={58} y={y + h - 24} rotate={18} s={1.1} C={C} />
      </g>;
    })}
    {W > 900 && <Script x={W * 0.56} y={ground + 2} lines={["Family isn’t just where life begins,", "it’s where it always belongs ♡"]} C={C} size={28} rotate={-4} />}
    {lines}
    {hearts}
    {placed.map((p) => {
      const x = px(p.id), y0 = top(p.id), a = ancestryOf(bloodlines, p.id);
      const active = focusId === p.id || sel === p.id, gone = isGone(p);
      const main = lineColour(C, a.dominant);
      return <Hit key={p.id} p={p} onPick={onPick} onHover={onHover}>
        <g data-trace-opacity opacity={dim(focusId, trace, p.id)}>
          {active && <rect data-selection-halo x={x - cw / 2 - 8} y={y0 - 8} width={cw + 16} height={ch + 16} rx={22} fill={C.tok("primary", 0.12)} stroke={C.tok("primary", 0.6)} strokeWidth={2.4} />}
          <rect x={x - cw / 2} y={y0 + 5} width={cw} height={ch} rx={16} fill={C.tok("tree-bark-dark", 0.08)} />
          <rect x={x - cw / 2} y={y0} width={cw} height={ch} rx={16} fill={C.tok("surface")} stroke={active ? C.tok("primary", 0.8) : C.tok("border")} strokeWidth={active ? 2 : 1} />
          <path d={`M${x - cw / 2 + 12} ${y0 + 1.5} H${x + cw / 2 - 12}`} stroke={main} strokeWidth={4} strokeLinecap="round" />
          <circle cx={x} cy={y0 + 50} r={28} fill={lineColour(C, a.dominant, C.dark ? 0.2 : 0.1)} />
          <LineageRing ancestry={a} C={C} x={x} y={y0 + 50} radius={29} width={3.5} />
          <text x={x} y={y0 + 57} textAnchor="middle" fontFamily={SERIF} fontSize={18} fontWeight={600} fill={main}>{initialsOf(p)}</text>
          <text x={x} y={y0 + 108} textAnchor="middle" fontFamily={HAND} fontSize={25} fontWeight={700} fill={C.tok("text")}>{short(textName(p), 12)}</text>
          {p.family_name && <text x={x} y={y0 + 127} textAnchor="middle" fontFamily={SANS} fontSize={12} fill={C.tok("text-muted")}>{short(p.family_name, 18)}</text>}
          <text x={x} y={y0 + (p.family_name ? 145 : 128)} textAnchor="middle" fontFamily={SANS} fontSize={12} fill={C.tok("text-muted")}>{yearLine(p)}</text>
          {gone && <>
            <Dove x={x - cw / 2 + 12} y={y0 + 16} fill={C.tok("text-muted", 0.55)} />
            <text x={x} y={y0 + ch + 24} textAnchor="middle" fontFamily={HAND} fontSize={18} fill={C.tok("text-muted")}>In loving memory</text>
          </>}
          <rect className="family-tree-focus" x={x - cw / 2 - 3} y={y0 - 3} width={cw + 6} height={ch + 6} rx={18}
            fill="transparent" stroke="transparent" pointerEvents="all" />
        </g>
      </Hit>;
    })}
    {void scale}
  </>;
});
